import React, { useEffect, useMemo, useState } from "react";
import { X, Search } from "lucide-react";
import { hqApi } from "@/hq/hqApi";
import { INDUSTRY_CATALOGUE, industryLabel } from "@/hq/industries";
import { toast } from "sonner";

// Phase 3I.4P.3 — Bulk Set Industry modal with searchable dropdown.
export default function BulkIndustryModal({ open, businessIds, onClose, onDone }) {
  const [query, setQuery] = useState("");
  const [industry, setIndustry] = useState("");
  const [saving, setSaving] = useState(false);
  const overCap = businessIds.length > 25;

  // Phase 3I.4P.3.2 — always open fresh: no stale search text, no
  // pre-highlighted industry from a previous session. Cancel/close
  // discards the temporary selection.
  useEffect(() => {
    if (!open) { setQuery(""); setIndustry(""); setSaving(false); return; }
    setQuery(""); setIndustry(""); setSaving(false);
  }, [open]);

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return INDUSTRY_CATALOGUE;
    return INDUSTRY_CATALOGUE.filter((row) =>
      row.label.toLowerCase().includes(q) || row.slug.toLowerCase().includes(q)
    );
  }, [query]);

  const canApply = !!industry && !overCap && businessIds.length > 0 && !saving;

  if (!open) return null;

  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await hqApi.post("/control/clients/bulk-industry", {
        business_ids: businessIds, industry,
      });
      // Phase 3I.4P.3.1 — verify the backend actually matched rows before
      // showing success.
      const matched  = Number(data.matched_count ?? data.updated ?? 0);
      const modified = Number(data.modified_count ?? matched);
      if (matched <= 0) {
        toast.error("No clients were updated. Please try again.");
        return;
      }
      const label = industryLabel(industry);
      toast.success(
        modified === matched
          ? `Industry set to ${label} · ${matched} client${matched === 1 ? "" : "s"} updated`
          : `Industry set to ${label} · ${modified} updated, ${matched - modified} already ${label}`
      );
      onDone?.(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Bulk industry update failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="bulk-industry-modal">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Bulk Update</div>
            <div className="font-black text-lg">Set industry · {businessIds.length} client{businessIds.length === 1 ? "" : "s"}</div>
          </div>
          <button onClick={onClose} data-testid="bulk-industry-close" className="p-1 rounded hover:bg-[#F6F5F2]"><X size={18}/></button>
        </div>
        <div className="p-6 space-y-4">
          {overCap && (
            <div className="p-3 rounded-lg bg-[#FDECEC] text-[#C1350B] text-sm border border-[#F5C6C6]">
              Maximum 25 clients per bulk action. Reduce the selection first.
            </div>
          )}
          <label className="block">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Search industry</div>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]"/>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to filter (e.g. super, real, health…)"
                data-testid="bulk-industry-search"
                className="w-full pl-9 pr-3 py-2 rounded-lg border"
              />
            </div>
          </label>

          <div className="max-h-64 overflow-auto rounded-lg border border-[#EAEAEA]" data-testid="bulk-industry-list">
            {options.length === 0 ? (
              <div className="px-3 py-4 text-[12px] text-[#8A8A8A]">No industries match.</div>
            ) : options.map((row) => (
              <button
                key={row.slug}
                type="button"
                onClick={() => setIndustry(row.slug)}
                data-testid={`bulk-industry-option-${row.slug}`}
                className={`w-full text-left px-3 py-2 text-[13px] border-b border-[#EAEAEA] last:border-0 hover:bg-[#FFF7EE] ${industry === row.slug ? "bg-[#FFF7EE] font-black" : ""}`}
              >
                {row.label}
                {industry === row.slug && <span className="ml-2 text-[#FF6A00]">✓</span>}
              </button>
            ))}
          </div>

          <div className="text-[12px] text-[#8A8A8A]">
            Only the Industry field on the selected clients will change.
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#EAEAEA] flex justify-end gap-2">
          <button onClick={onClose} data-testid="bulk-industry-cancel"
            className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase">
            Cancel
          </button>
          <button onClick={apply} disabled={!canApply} data-testid="bulk-industry-apply"
            className={`px-5 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${canApply ? "bg-[#FF6A00] text-black hover:bg-[#e75f00]" : "bg-[#EAEAEA] text-[#8A8A8A] cursor-not-allowed"}`}>
            {saving ? "Applying…" : `Apply to ${businessIds.length} client${businessIds.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
